import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
  inject,
} from '@angular/core';
import {
  ConfirmDialog,
  ConfirmDialogType,
} from 'ui/confirm-dialog/confirm-dialog';
import { DialogOverlayService } from 'core/services/dialog-overlay.service';

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  /** Texto em destaque (itálico) exibido após o message. */
  messageEmphasis?: string;
  confirmText?: string;
  cancelText?: string;
  /** Quando false, oculta o botão secundário (ex.: modal só com OK). */
  showCancel?: boolean;
  /**
   * Quando false, o modal só fecha pelo botão de confirmação
   * (clique fora e Escape são ignorados).
   */
  dismissible?: boolean;
  type?: ConfirmDialogType;
}

export interface PromptDialogOptions extends ConfirmDialogOptions {
  messageEmphasis?: string;
  promptLabel?: string;
  promptPlaceholder?: string;
  promptRequired?: boolean;
  promptMaxLength?: number;
  promptRequiredMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConfirmDialogService {
  private readonly overlayService = inject(DialogOverlayService);
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  private componentRef: ComponentRef<ConfirmDialog> | null = null;
  private containerElement: HTMLElement | null = null;
  private escapeHandler: ((event: KeyboardEvent) => void) | null = null;

  confirm(options: ConfirmDialogOptions): Promise<boolean> {
    return this.open(options).then((value) => value !== null);
  }

  prompt(options: PromptDialogOptions): Promise<string | null> {
    return this.open({
      ...options,
      promptEnabled: true,
    });
  }

  private open(
    options: PromptDialogOptions & { promptEnabled?: boolean },
  ): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
      const dismissible = options.dismissible !== false;
      const overlay = this.overlayService.createOverlay(
        dismissible
          ? () => {
              this.close();
              resolve(null);
            }
          : undefined,
      );

      this.containerElement = document.createElement('div');
      this.containerElement.className = 'w-full max-w-md';
      overlay.appendChild(this.containerElement);

      this.componentRef = createComponent(ConfirmDialog, {
        environmentInjector: this.injector,
      });

      this.componentRef.setInput(
        'title',
        options.title ?? 'Confirmar exclusão',
      );
      this.componentRef.setInput('message', options.message);
      this.componentRef.setInput(
        'messageEmphasis',
        options.messageEmphasis ?? '',
      );
      this.componentRef.setInput(
        'confirmText',
        options.confirmText ?? 'Excluir',
      );
      const showCancel = options.showCancel !== false;
      this.componentRef.setInput(
        'cancelText',
        showCancel ? (options.cancelText ?? 'Cancelar') : '',
      );
      this.componentRef.setInput('type', options.type ?? 'danger');
      this.componentRef.setInput(
        'promptEnabled',
        options.promptEnabled ?? false,
      );
      this.componentRef.setInput(
        'promptLabel',
        options.promptLabel ?? 'Justificativa',
      );
      this.componentRef.setInput(
        'promptPlaceholder',
        options.promptPlaceholder ?? '',
      );
      this.componentRef.setInput(
        'promptRequired',
        options.promptRequired ?? true,
      );
      this.componentRef.setInput(
        'promptMaxLength',
        options.promptMaxLength ?? 2000,
      );
      this.componentRef.setInput(
        'promptRequiredMessage',
        options.promptRequiredMessage ?? 'Informe a justificativa.',
      );

      this.componentRef.instance.confirmed.subscribe((value) => {
        this.close();
        resolve(value);
      });

      this.componentRef.instance.cancelled.subscribe(() => {
        this.close();
        resolve(null);
      });

      this.appRef.attachView(this.componentRef.hostView);
      this.containerElement.appendChild(
        this.componentRef.location.nativeElement,
      );

      if (dismissible) {
        this.escapeHandler = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            this.close();
            resolve(null);
          }
        };
        document.addEventListener('keydown', this.escapeHandler);
      }

      setTimeout(() => {
        const prompt = this.containerElement?.querySelector(
          'input#confirm-dialog-prompt',
        ) as HTMLElement | null;
        if (prompt) {
          prompt.focus();
          return;
        }
        const firstButton = this.containerElement?.querySelector(
          'button',
        ) as HTMLElement | null;
        firstButton?.focus();
      }, 100);
    });
  }

  private close(): void {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
      this.escapeHandler = null;
    }

    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }

    if (this.containerElement?.parentNode) {
      this.containerElement.parentNode.removeChild(this.containerElement);
      this.containerElement = null;
    }

    this.overlayService.removeOverlay();
  }
}
